import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, Calendar, FileDown, Pencil, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { usePdfExport } from '@/hooks/usePdfExport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ScheduleManagement() {
  const [schedules, setSchedules] = useState([]);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [dispatchDialog, setDispatchDialog] = useState(false);
  const userObj = JSON.parse(localStorage.getItem('user') || '{}');
  const canModify = (entry) => userObj.role === 'admin' || entry.created_by === userObj.username;
  const { exporting, handleExport: exportPdf } = usePdfExport('/export/schedule-pdf', 'Schedule');
  const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0, 7));
  const [formData, setFormData] = useState({
    part_id: '', customer_id: '', month: new Date().toISOString().slice(0, 7),
    scheduled_qty: '', schedule_type: 'original'
  });
  const [editData, setEditData] = useState({ scheduled_qty: '', schedule_type: 'original', _original: null });
  const [dispatchData, setDispatchData] = useState({ date: new Date().toISOString().split('T')[0], planned_qty: '', _schedule: null });
  const [dispatchHistory, setDispatchHistory] = useState([]);
  const [customerFilter, setCustomerFilter] = useState('all');

  const headers = () => ({});

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/schedule?month=${filterMonth}`, {});
      setSchedules(res.data);
    } catch (err) { console.error(err); toast.error('Failed to fetch schedules'); }
    finally { setLoading(false); }
  }, [filterMonth]);

  const fetchItems = useCallback(async () => {
    try { const res = await axios.get(`${API}/masters/items`, {}); setItems(res.data); } catch (err) { console.error("Failed to load dropdown data:", err); }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try { const res = await axios.get(`${API}/masters/customers`, {}); setCustomers(res.data); } catch (err) { console.error("Failed to load dropdown data:", err); }
  }, []);

  useEffect(() => {
    fetchSchedules();
    fetchItems();
    fetchCustomers();
  }, [filterMonth, fetchSchedules, fetchItems, fetchCustomers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await axios.post(`${API}/schedule`, { ...formData, scheduled_qty: parseInt(formData.scheduled_qty), created_by: user.username }, { headers: headers() });
      toast.success('Schedule created successfully');
      setDialogOpen(false);
      resetForm();
      fetchSchedules();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create schedule'); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    const s = editData._original;
    try {
      await axios.put(`${API}/schedule-by-id/${s.id}`, {
        scheduled_qty: parseInt(editData.scheduled_qty),
        schedule_type: editData.schedule_type,
        customer_id: editData.customer_id
      });
      toast.success('Schedule updated');
      setEditDialog(false);
      fetchSchedules();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update schedule'); }
  };

  const handleDelete = async (schedule) => {
    if (!window.confirm('Delete this schedule?')) return;
    try {
      await axios.delete(`${API}/schedule-by-id/${schedule.id}`);
      toast.success('Schedule deleted');
      fetchSchedules();
    } catch (err) { console.error(err); toast.error(err.response?.data?.detail || 'Failed to delete schedule'); }
  };

  const openEdit = (schedule) => {
    setEditData({
      scheduled_qty: schedule.scheduled_qty.toString(),
      schedule_type: schedule.schedule_type,
      customer_id: schedule.customer_id,
      _original: schedule
    });
    setEditDialog(true);
  };

  const openDispatchPlan = async (schedule) => {
    setDispatchData({ date: new Date().toISOString().split('T')[0], planned_qty: '', _schedule: schedule });
    // Fetch existing dispatch plans for this part+customer in this month
    try {
      const month = schedule.month;
      const [year, mon] = month.split('-');
      const daysInMonth = new Date(parseInt(year), parseInt(mon), 0).getDate();
      const startDate = `${month}-01`;
      const endDate = `${month}-${String(daysInMonth).padStart(2, '0')}`;
      const res = await axios.get(`${API}/dispatch-plan?start_date=${startDate}&end_date=${endDate}`, { headers: headers() });
      const matching = res.data.filter(p => p.part_id === schedule.part_id && p.customer_id === schedule.customer_id);
      setDispatchHistory(matching);
    } catch (err) { console.error(err); setDispatchHistory([]); }
    setDispatchDialog(true);
  };

  const handleAddDispatch = async (e) => {
    e.preventDefault();
    const s = dispatchData._schedule;
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await axios.post(`${API}/dispatch-plan`, {
        date: dispatchData.date, part_id: s.part_id, customer_id: s.customer_id,
        planned_qty: parseInt(dispatchData.planned_qty), created_by: user.username
      }, { headers: headers() });
      toast.success('Dispatch plan added');
      setDispatchData({ ...dispatchData, planned_qty: '' });
      // Refresh dispatch history
      openDispatchPlan(s);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add dispatch plan'); }
  };

  const handleDeleteDispatch = async (id) => {
    try {
      await axios.delete(`${API}/dispatch-plan/${id}`, { headers: headers() });
      toast.success('Dispatch plan removed');
      if (dispatchData._schedule) openDispatchPlan(dispatchData._schedule);
    } catch (err) { console.error(err); toast.error('Failed to delete'); }
  };

  const resetForm = () => setFormData({ part_id: '', customer_id: '', month: filterMonth, scheduled_qty: '', schedule_type: 'original' });
  const getCustomerName = (id) => customers.find(c => c.customer_id === id)?.customer_name || id;
  const getItemName = (id) => items.find(i => i.part_id === id)?.part_name || '';

  const filteredSchedules = customerFilter === 'all' ? schedules : schedules.filter(s => s.customer_id === customerFilter);

  const totalPlanned = dispatchHistory.reduce((s, p) => s + p.planned_qty, 0);
  const remaining = dispatchData._schedule ? dispatchData._schedule.scheduled_qty - totalPlanned : 0;

  return (
    <div className="space-y-6" data-testid="schedule-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="schedule-management-title">PRODUCTION SCHEDULE</h1>
          <p className="text-sm text-text-muted mt-2">Manage monthly production schedules</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="export-pdf-button" onClick={() => exportPdf({ month: filterMonth })} disabled={exporting || schedules.length === 0} className="bg-green-600 text-white hover:bg-green-700 rounded-sm font-medium">
            <FileDown className="w-4 h-4 mr-2" />{exporting ? 'EXPORTING...' : 'EXPORT PDF'}
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-schedule-button" onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-accent text-accent-foreground hover:bg-accent-hover rounded-sm font-medium">
                <Plus className="w-4 h-4 mr-2" />ADD SCHEDULE
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>NEW SCHEDULE</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Part</Label>
                    <Select value={formData.part_id} onValueChange={(v) => setFormData({ ...formData, part_id: v })} required>
                      <SelectTrigger data-testid="schedule-part-select" className="mt-1.5 rounded-none"><SelectValue placeholder="Select part" /></SelectTrigger>
                      <SelectContent>{items.map(i => <SelectItem key={i.part_id} value={i.part_id}>{i.part_id} - {i.part_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Customer</Label>
                    <Select value={formData.customer_id} onValueChange={(v) => setFormData({ ...formData, customer_id: v })} required>
                      <SelectTrigger data-testid="schedule-customer-select" className="mt-1.5 rounded-none"><SelectValue placeholder="Select customer" /></SelectTrigger>
                      <SelectContent>{customers.map(c => <SelectItem key={c.customer_id} value={c.customer_id}>{c.customer_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Month</Label>
                    <Input data-testid="schedule-month-input" type="month" value={formData.month} onChange={(e) => setFormData({ ...formData, month: e.target.value })} required className="mt-1.5 rounded-none" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Scheduled Quantity</Label>
                    <Input data-testid="schedule-qty-input" type="number" value={formData.scheduled_qty} onChange={(e) => setFormData({ ...formData, scheduled_qty: e.target.value })} required className="mt-1.5 rounded-none" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Schedule Type</Label>
                    <Select value={formData.schedule_type} onValueChange={(v) => setFormData({ ...formData, schedule_type: v })} required>
                      <SelectTrigger data-testid="schedule-type-select" className="mt-1.5 rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="original">Original Schedule</SelectItem>
                        <SelectItem value="enhanced">Enhanced Schedule (Mid-month revision)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" onClick={() => setDialogOpen(false)} className="bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-sm">CANCEL</Button>
                  <Button data-testid="submit-schedule-button" type="submit" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">CREATE</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white border border-border shadow-sm p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <Calendar className="w-5 h-5 text-text-muted" />
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Month</Label>
            <Input type="month" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} className="w-48 rounded-none mt-1" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Customer</Label>
            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger data-testid="schedule-customer-filter" className="w-56 rounded-none mt-1">
                <SelectValue placeholder="All Customers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Customers</SelectItem>
                {customers.map(c => (
                  <SelectItem key={c.customer_id} value={c.customer_id}>{c.customer_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Schedule Table */}
      <div className="bg-white border border-border shadow-sm">
        {loading ? (
          <div className="p-12 text-center text-text-muted">Loading schedules...</div>
        ) : filteredSchedules.length === 0 ? (
          <div className="p-12 text-center text-text-muted">
            {customerFilter !== 'all' ? 'No schedules found for selected customer in ' + filterMonth : 'No schedules found for ' + filterMonth + '.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full caption-bottom text-sm" data-testid="schedule-table">
              <thead>
                <tr className="border-b">
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Part ID</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Part Name</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Customer</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Scheduled Qty</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Planned Qty</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Yet to Plan</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Type</th>
                  <th className="h-10 px-4 text-center align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSchedules.map((s, idx) => (
                  <tr key={idx} className={`border-b transition-colors hover:bg-muted/50 ${s.planned_qty === 0 ? 'bg-red-50/60' : ''}`} data-testid={`schedule-row-${idx}`}>
                    <td className="p-4 align-middle font-mono text-xs font-semibold">{s.part_id}</td>
                    <td className="p-4 align-middle text-xs text-text-muted">{getItemName(s.part_id)}</td>
                    <td className="p-4 align-middle text-xs">{getCustomerName(s.customer_id)}</td>
                    <td className="p-4 align-middle text-right font-mono text-xs font-semibold">{s.scheduled_qty.toLocaleString()}</td>
                    <td className="p-4 align-middle text-right font-mono text-xs font-semibold" data-testid={`planned-qty-${idx}`}>
                      {s.planned_qty === 0 ? (
                        <span className="text-red-600 font-bold">0</span>
                      ) : (
                        <span className={s.planned_qty >= s.scheduled_qty ? 'text-green-600' : 'text-blue-600'}>{s.planned_qty.toLocaleString()}</span>
                      )}
                    </td>
                    <td className="p-4 align-middle text-right font-mono text-xs font-semibold" data-testid={`yet-to-plan-${idx}`}>
                      {s.yet_to_plan > 0 ? (
                        <span className="text-orange-600">{s.yet_to_plan.toLocaleString()}</span>
                      ) : (
                        <span className="text-green-600">0</span>
                      )}
                    </td>
                    <td className="p-4 align-middle">
                      <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent ${s.schedule_type === 'enhanced' ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-700'}`}>
                        {s.schedule_type === 'enhanced' ? 'Enhanced' : 'Original'}
                      </span>
                    </td>
                    <td className="p-4 align-middle">
                      <div className="flex items-center justify-center gap-1">
                        {canModify(s) && (
                        <button data-testid={`edit-schedule-${idx}`} onClick={() => openEdit(s)} className="p-2 hover:bg-blue-50 hover:text-blue-600 transition-all rounded-sm" title="Edit Schedule">
                          <Pencil className="w-4 h-4" />
                        </button>
                        )}
                        <button data-testid={`plan-dispatch-${idx}`} onClick={() => openDispatchPlan(s)} className="p-2 hover:bg-green-50 hover:text-green-600 transition-all rounded-sm" title="Plan Dispatch">
                          <Truck className="w-4 h-4" />
                        </button>
                        {canModify(s) && (
                        <button data-testid={`delete-schedule-${idx}`} onClick={() => handleDelete(s)} className="p-2 hover:bg-error/10 hover:text-error transition-all rounded-sm" title="Delete Schedule">
                          <Trash2 className="w-4 h-4" />
                        </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>EDIT SCHEDULE</DialogTitle>
          </DialogHeader>
          {editData._original && (
            <div className="space-y-1 mb-4 bg-gray-50 rounded-sm p-3 border">
              <p className="text-xs text-text-muted"><span className="font-semibold">Part:</span> {editData._original.part_id} — {getItemName(editData._original.part_id)}</p>
              <p className="text-xs text-text-muted"><span className="font-semibold">Customer:</span> {getCustomerName(editData._original.customer_id)}</p>
              <p className="text-xs text-text-muted"><span className="font-semibold">Month:</span> {editData._original.month}</p>
            </div>
          )}
          <form onSubmit={handleEdit} className="space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Customer</Label>
              <Select value={editData.customer_id || ''} onValueChange={(v) => setEditData({ ...editData, customer_id: v })}>
                <SelectTrigger data-testid="edit-customer-select" className="mt-1.5 rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>{customers.map(c => <SelectItem key={c.customer_id} value={c.customer_id}>{c.customer_id} — {c.customer_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Scheduled Quantity</Label>
              <Input data-testid="edit-qty-input" type="number" value={editData.scheduled_qty} onChange={(e) => setEditData({ ...editData, scheduled_qty: e.target.value })} required className="mt-1.5 rounded-none" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Schedule Type</Label>
              <Select value={editData.schedule_type} onValueChange={(v) => setEditData({ ...editData, schedule_type: v })}>
                <SelectTrigger data-testid="edit-type-select" className="mt-1.5 rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="original">Original</SelectItem>
                  <SelectItem value="enhanced">Enhanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" onClick={() => setEditDialog(false)} className="bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-sm">CANCEL</Button>
              <Button data-testid="save-edit-button" type="submit" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">SAVE</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dispatch Plan Dialog */}
      <Dialog open={dispatchDialog} onOpenChange={setDispatchDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>PLAN DISPATCH</DialogTitle>
          </DialogHeader>
          {dispatchData._schedule && (
            <>
              <div className="bg-gray-50 rounded-sm p-3 border space-y-1">
                <p className="text-xs text-text-muted"><span className="font-semibold">Part:</span> {dispatchData._schedule.part_id} — {getItemName(dispatchData._schedule.part_id)}</p>
                <p className="text-xs text-text-muted"><span className="font-semibold">Customer:</span> {getCustomerName(dispatchData._schedule.customer_id)}</p>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <div className="bg-white border rounded-sm p-3 text-center">
                  <p className="text-xs text-text-muted uppercase font-medium">Scheduled</p>
                  <p className="text-xl font-bold font-mono">{dispatchData._schedule.scheduled_qty.toLocaleString()}</p>
                </div>
                <div className="bg-white border rounded-sm p-3 text-center">
                  <p className="text-xs text-text-muted uppercase font-medium">Planned</p>
                  <p className="text-xl font-bold font-mono text-blue-600">{totalPlanned.toLocaleString()}</p>
                </div>
                <div className={`border rounded-sm p-3 text-center ${remaining > 0 ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'}`}>
                  <p className="text-xs text-text-muted uppercase font-medium">Remaining</p>
                  <p className={`text-xl font-bold font-mono ${remaining > 0 ? 'text-orange-600' : 'text-green-600'}`}>{remaining.toLocaleString()}</p>
                </div>
              </div>

              {/* Add new dispatch entry */}
              {remaining > 0 && (
                <form onSubmit={handleAddDispatch} className="flex items-end gap-3 mt-4 p-3 bg-blue-50/50 border border-blue-100 rounded-sm">
                  <div className="flex-1">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Dispatch Date</Label>
                    <Input data-testid="dispatch-date-input" type="date" value={dispatchData.date} onChange={(e) => setDispatchData({ ...dispatchData, date: e.target.value })} required className="mt-1 rounded-none" />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Quantity (max {remaining})</Label>
                    <Input data-testid="dispatch-qty-input" type="number" min="1" max={remaining} value={dispatchData.planned_qty} onChange={(e) => setDispatchData({ ...dispatchData, planned_qty: e.target.value })} required className="mt-1 rounded-none" />
                  </div>
                  <Button data-testid="add-dispatch-button" type="submit" className="bg-accent text-accent-foreground hover:bg-accent-hover rounded-sm font-medium">
                    <Plus className="w-4 h-4 mr-1" />ADD
                  </Button>
                </form>
              )}
              {remaining <= 0 && totalPlanned > 0 && (
                <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-sm p-3 mt-3 font-medium text-center">
                  Full schedule quantity has been planned for dispatch
                </p>
              )}

              {/* Existing dispatch plan entries */}
              {dispatchHistory.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-2">Planned Dispatches</h3>
                  <div className="border rounded-sm overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50/50">
                          <th className="h-8 px-3 text-left font-medium text-muted-foreground uppercase text-xs tracking-wider">Date</th>
                          <th className="h-8 px-3 text-right font-medium text-muted-foreground uppercase text-xs tracking-wider">Qty</th>
                          <th className="h-8 px-3 text-center font-medium text-muted-foreground uppercase text-xs tracking-wider w-16"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {dispatchHistory.map((d, i) => (
                          <tr key={i} className="border-b last:border-0" data-testid={`dispatch-entry-${i}`}>
                            <td className="px-3 py-2 font-mono text-xs">{d.date}</td>
                            <td className="px-3 py-2 text-right font-mono text-xs font-semibold">{d.planned_qty.toLocaleString()}</td>
                            <td className="px-3 py-2 text-center">
                              {canModify(d) && (
                              <button onClick={() => handleDeleteDispatch(d.id)} className="p-1 hover:bg-error/10 hover:text-error transition-all rounded-sm">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
